import * as Icon from "@ant-design/icons";
import { Button, Col, Divider, Flex, Form, Modal, Row, Space, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PagamentoContaFinanceiraVWModel } from "../../../../models/pagamentoContaFinanceira/pagamentoContaFinanceiraVWModel";
import { setTitle } from "../../../../redux/slices/configuracoes/configuracoesSlice";
import { currencyBRL } from "../../../../util/dinheiroBRAuxiliar";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import CadastroPagamento from "./modals/cadastrar";
import DeletarPagamentoLote from "./modals/deletarLote";
import { setPageContaFinanceira } from "../../../../redux/slices/contaFinanceira/contaFinanceiraSlice";
import {
  setEntityPagamentoContaFinanceira,
  setModalDeletarLote,
  setModalDeletarpagamentoContaFinanceira,
  setModalPagamentoUnico,
} from "../../../../redux/slices/pagamentoContaFinanceira/pagamentoContaFinanceiraSlice";
import TableComponent from "../../../../components/table/tableComponent";
import { ItemMenuAcoes } from "../../../../models/sistema/itemMenuAcoes";
import * as PhosphorIcon from "phosphor-react";
import AcoesComponent from "../../../../components/table/acoesComponent";
import dayjs from "dayjs";
import { buscarPagamentoContaFinanceira, deletarPagamentoContaFinanceira } from "../../../../services/pagamentoContaFinanceiraService";
import MensagemModal from "../../../../components/modal/mensagem";

dayjs.locale("pt-br");

const VisualizarPagamentosPage = () => {
  //estados globais
  const pagamentoContaFinanceiraState = useAppSelector((state) => state.pagamentoContaFinanceira);
  const contaFinanceiraState = useAppSelector((state) => state.contaFinanceira);

  //hooks
  const dispatch = useAppDispatch();
  const location = useLocation();

  //estados locais
  const editar = Object.values(pagamentoContaFinanceiraState.entity).length !== 0;

  const despesa = location.pathname === "/contaPagar";

  useEffect(() => {
    dispatch(setTitle("VISUALIZAR PAGAMENTOS"));
    dispatch(buscarPagamentoContaFinanceira(contaFinanceiraState.entity.id));
  }, [contaFinanceiraState.entity]);

  const goCadastrar = () => {
    dispatch(setEntityPagamentoContaFinanceira({}));
    dispatch(setModalPagamentoUnico(true));
  };

  const goEditar = (value: PagamentoContaFinanceiraVWModel) => {
    dispatch(setEntityPagamentoContaFinanceira(value));
    dispatch(setModalPagamentoUnico(true));
  };

  const closeModalCadastrar = () => {
    dispatch(setModalPagamentoUnico(false));
  };

  const goDeletar = (value: PagamentoContaFinanceiraVWModel) => {
    dispatch(setEntityPagamentoContaFinanceira(value));
    dispatch(setModalDeletarpagamentoContaFinanceira(true));
  };

  const closeModalDeletar = () => {
    dispatch(setModalDeletarpagamentoContaFinanceira(false));
  };

  const goDeletarLote = (value: PagamentoContaFinanceiraVWModel) => {
    dispatch(setEntityPagamentoContaFinanceira(value));
    dispatch(setModalDeletarLote(true));
  };

  const closeModalDeletarLote = () => {
    dispatch(setModalDeletarLote(false));
  };

  const voltar = () => {
    dispatch(setPageContaFinanceira("geral"));
    dispatch(setTitle(despesa ? "CONTAS A PAGAR" : "CONTAS A RECEBER"));
  };

  const items: ItemMenuAcoes[] = [
    {
      label: "Editar",
      key: "1",
      icon: <PhosphorIcon.Pencil size={16} color="#212937" weight="light" />,
      function: (item: any) => goEditar(item),
    },
    {
      label: "Remover",
      key: "2",
      icon: <PhosphorIcon.XCircle size={16} color="#212937" weight="light" />,
      function: (item: any) => goDeletar(item),
    },
  ];

  const itemsLote: ItemMenuAcoes[] = [
    {
      label: "Remover",
      key: "2",
      icon: <PhosphorIcon.XCircle size={16} color="#212937" weight="light" />,
      function: (item: any) => goDeletarLote(item),
    },
  ];

  const columns: ColumnsType<PagamentoContaFinanceiraVWModel> = [
    {
      title: "DATA",
      dataIndex: "data, tipo",
      render: (_, record) => <>{dayjs(record.data).format("DD/MM/YYYY")}</>,
    },
    {
      title: "CONTA BANCÁRIA",
      dataIndex: "contaBanco",
      sorter: (a, b) => a.contaBanco.localeCompare(b.contaBanco),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: "TIPO",
      dataIndex: "formaPagamentoRecebimento",
      align: "center",
    },
    {
      title: "PRINCIPAL",
      dataIndex: "valor",
      align: "right",
      sorter: (a: any, b: any) => a.valor - b.valor,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.valor),
    },
    {
      title: "JUROS",
      dataIndex: "juros",
      align: "right",
      sorter: (a: any, b: any) => a.juros - b.juros,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.juros),
    },
    {
      title: "MULTA",
      dataIndex: "multa",
      align: "right",
      sorter: (a: any, b: any) => a.multa - b.multa,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.multa),
    },
    {
      title: "DESCONTO",
      dataIndex: "desconto",
      align: "right",
      sorter: (a: any, b: any) => a.desconto - b.desconto,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.desconto),
    },
    {
      title: "OUTROS VALORES",
      dataIndex: "outrosValores",
      align: "right",
      sorter: (a: any, b: any) => a.outrosValores - b.outrosValores,
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => currencyBRL(record.outrosValores),
    },
    {
      title: "HISTÓRICO",
      dataIndex: "historico",
      align: "center",
      sorter: (a: any, b: any) => a.historico.localeCompare(b.historico),
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) =>
        record.historico && (
          <Tooltip placement="top" title={record.historico}>
            <Button>
              <Icon.FileTextOutlined />
            </Button>
          </Tooltip>
        ),
    },
    {
      title: "AÇÕES",
      dataIndex: "acoes",
      align: "center",
      fixed: "right",
      width: 75,
      onCell: (record) => {
        return {
          onClick: (event) => {
            event.stopPropagation();
          },
        };
      },
      render: (_, record) => <AcoesComponent record={record} items={record.idPagamentoLote ? itemsLote : items} />,
    },
  ];

  return (
    <Flex vertical>
      <Flex onClick={voltar} className="voltar">
        {"<"} VOLTAR
      </Flex>
      <Form layout="vertical">
        <Row gutter={12} align="bottom">
          <Col span={24}>
            <Flex justify="end">
              <Form.Item label=" ">
                <Button className="addButton buttonPrimary" onClick={goCadastrar} icon={<Icon.PlusCircleOutlined />}>
                  NOVO PAGAMENTO
                </Button>
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
      <TableComponent data={pagamentoContaFinanceiraState.list} columns={columns} loading={pagamentoContaFinanceiraState.loading} />
      <Divider />
      <Row gutter={12}>
        <Col span={24} className="modalFooter">
          <Space size="middle">
            <Button className="cancelButton" onClick={voltar} icon={<Icon.LeftOutlined />}>
              VOLTAR
            </Button>
          </Space>
        </Col>
      </Row>
      <Modal
        title={editar ? "EDITAR PAGAMENTO" : "NOVO PAGAMENTO"}
        centered
        width={1100}
        open={pagamentoContaFinanceiraState.modalPagamentoUnico}
        onCancel={closeModalCadastrar}
        footer={null}
        destroyOnHidden
      >
        <CadastroPagamento />
      </Modal>
      <Modal title={"REMOVER PAGAMENTO"} centered width={350} open={pagamentoContaFinanceiraState.modalDeletar} onCancel={closeModalDeletar} footer={null} destroyOnHidden>
        <MensagemModal
          text="Deseja remover o pagamento?"
          action={deletarPagamentoContaFinanceira({ value: pagamentoContaFinanceiraState.entity.idContaFinanceira, busca: pagamentoContaFinanceiraState.entity.idContaFinanceira })}
          close={setModalDeletarpagamentoContaFinanceira(false)}
        />
      </Modal>
      <Modal title={"REMOVER PAGAMENTO MÚLTIPLO"} centered width={1000} open={pagamentoContaFinanceiraState.modalDeletarLote} onCancel={closeModalDeletarLote} footer={null} destroyOnHidden>
        <DeletarPagamentoLote />
      </Modal>
    </Flex>
  );
};

export default VisualizarPagamentosPage;
